import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TableDataService } from '../../services/tableData.service';
import { TableData, TableDataHistory, TableDataQuery, TableDataResponse } from '../../types/tableData.types';
import { RootState } from '../index';

// Интерфейс состояния
interface TableDataState {
  data: TableData[];
  currentData: TableData | null;
  dataHistory: TableDataHistory[];
  totalCount: number;
  isLoading: boolean;
  error: string | null;
}

// Начальное состояние
const initialState: TableDataState = {
  data: [],
  currentData: null,
  dataHistory: [],
  totalCount: 0,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchTableData = createAsyncThunk(
  'tableData/fetchAll',
  async ({ tableId, query }: { tableId: string; query?: TableDataQuery }, { rejectWithValue }) => {
    try {
      const response = await TableDataService.getTableData(tableId, query);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки данных');
    }
  }
);

export const fetchTableDataById = createAsyncThunk(
  'tableData/fetchById',
  async ({ tableId, dataId }: { tableId: string; dataId: string }, { rejectWithValue }) => {
    try {
      const response = await TableDataService.getTableDataById(tableId, dataId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки данных');
    }
  }
);

export const createTableData = createAsyncThunk(
  'tableData/create',
  async ({ tableId, data }: { tableId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await TableDataService.createTableData(tableId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка создания записи');
    }
  }
);

export const updateTableData = createAsyncThunk(
  'tableData/update',
  async ({ tableId, dataId, data }: { tableId: string; dataId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await TableDataService.updateTableData(tableId, dataId, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка обновления записи');
    }
  }
);

export const deleteTableData = createAsyncThunk(
  'tableData/delete',
  async ({ tableId, dataId }: { tableId: string; dataId: string }, { rejectWithValue }) => {
    try {
      await TableDataService.deleteTableData(tableId, dataId);
      return { tableId, dataId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка удаления записи');
    }
  }
);

export const fetchTableDataHistory = createAsyncThunk(
  'tableData/fetchHistory',
  async ({ tableId, dataId }: { tableId: string; dataId: string }, { rejectWithValue }) => {
    try {
      const response = await TableDataService.getTableDataHistory(tableId, dataId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Ошибка загрузки истории изменений');
    }
  }
);

// Slice
const tableDataSlice = createSlice({
  name: 'tableData',
  initialState,
  reducers: {
    clearTableDataError: (state) => {
      state.error = null;
    },
    clearCurrentData: (state) => {
      state.currentData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all table data
      .addCase(fetchTableData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTableData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.total;
      })
      .addCase(fetchTableData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch table data by ID
      .addCase(fetchTableDataById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTableDataById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentData = action.payload;
      })
      .addCase(fetchTableDataById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Create table data
      .addCase(createTableData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createTableData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createTableData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update table data
      .addCase(updateTableData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTableData.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        if (state.currentData && state.currentData.id === action.payload.id) {
          state.currentData = action.payload;
        }
      })
      .addCase(updateTableData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete table data
      .addCase(deleteTableData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTableData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = state.data.filter(item => item.id !== action.payload.dataId);
        if (state.currentData && state.currentData.id === action.payload.dataId) {
          state.currentData = null;
        }
        state.totalCount -= 1;
      })
      .addCase(deleteTableData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch table data history
      .addCase(fetchTableDataHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTableDataHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dataHistory = action.payload;
      })
      .addCase(fetchTableDataHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearTableDataError, clearCurrentData } = tableDataSlice.actions;

export const selectTableData = (state: RootState) => state.tableData.data;
export const selectCurrentData = (state: RootState) => state.tableData.currentData;
export const selectDataHistory = (state: RootState) => state.tableData.dataHistory;
export const selectDataTotalCount = (state: RootState) => state.tableData.totalCount;
export const selectDataLoading = (state: RootState) => state.tableData.isLoading;
export const selectDataError = (state: RootState) => state.tableData.error;

export default tableDataSlice.reducer;